/**
 * AdminModerationFeedback — /admin/moderation/feedback
 * Shows the moderator feedback loop insights: how often moderators
 * override the AI, per target type, and the auto-tuned confidence
 * thresholds derived from those overrides.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, Brain, Loader2, TrendingUp, TrendingDown, Minus, RefreshCw } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

const API = process.env.REACT_APP_BACKEND_URL;

export default function AdminModerationFeedback() {
  const { user, token } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/api/photos/moderation/feedback?limit=500`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setLoading(false); }
  };
  useEffect(() => { if (token) load(); }, [token]);

  if (!user?.is_admin && user?.role !== "admin") {
    return <div className="max-w-xl mx-auto py-16 px-4 text-center"><h1 className="font-display text-3xl">Admin only</h1><Link to="/" className="wofy-link">← Home</Link></div>;
  }

  return (
    <div className="max-w-5xl mx-auto py-8 px-4" data-testid="admin-moderation-feedback">
      <Link to="/admin/photos" className="inline-flex items-center gap-1 text-sm opacity-70 hover:opacity-100 mb-4">
        <ArrowLeft className="w-4 h-4" /> Photo Submissions
      </Link>

      <div className="flex items-start justify-between flex-wrap gap-3 mb-4">
        <div>
          <div className="overline text-xs opacity-70">ADMIN · AI TRAINING</div>
          <h1 className="font-display text-3xl flex items-center gap-2"><Brain className="w-7 h-7" />Moderator Feedback Loop</h1>
          <p className="text-sm opacity-70 mt-1 max-w-2xl">
            When you override the AI (approving a false rejection, or rejecting a false approval), we log it here.
            Every time you refresh, the recommended auto-approve confidence thresholds are re-tuned per content type
            — so the model gets smarter based on your team's real decisions.
          </p>
        </div>
        <button onClick={load} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-sm" style={{ borderColor: "var(--wofy-border)" }} data-testid="feedback-refresh">
          <RefreshCw className="w-4 h-4" />Refresh & retune
        </button>
      </div>

      {loading && !data && (
        <div className="text-center py-10 opacity-60"><Loader2 className="w-6 h-6 mx-auto animate-spin" /></div>
      )}

      {data && (
        <>
          {/* Aggregate override counts */}
          <section className="wofy-card !p-5 mb-6" data-testid="feedback-summary">
            <h2 className="font-display text-xl mb-3">Overrides so far</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-center">
              <div className="p-3 rounded-lg" style={{ background: "#DCFCE7" }}>
                <div className="text-2xl font-display">{data.by_override?.approve_after_reject || 0}</div>
                <div className="text-xs opacity-70">Approved after AI reject</div>
              </div>
              <div className="p-3 rounded-lg" style={{ background: "#DBEAFE" }}>
                <div className="text-2xl font-display">{data.by_override?.approve_after_pending || 0}</div>
                <div className="text-xs opacity-70">Approved from pending</div>
              </div>
              <div className="p-3 rounded-lg" style={{ background: "#FEE2E2" }}>
                <div className="text-2xl font-display">{data.by_override?.reject_after_approve || 0}</div>
                <div className="text-xs opacity-70">Rejected after AI approve</div>
              </div>
              <div className="p-3 rounded-lg" style={{ background: "var(--wofy-surface)" }}>
                <div className="text-2xl font-display">{data.total}</div>
                <div className="text-xs opacity-70">Total feedback events</div>
              </div>
            </div>
          </section>

          {/* Per-target suggestions */}
          <section className="wofy-card !p-5 mb-6">
            <h2 className="font-display text-xl mb-3">Auto-tuned thresholds by content type</h2>
            {Object.keys(data.suggestions || {}).length === 0 ? (
              <div className="opacity-70 text-sm">No override events yet — the AI will use the default threshold (0.90) everywhere.</div>
            ) : (
              <div className="grid gap-2">
                {Object.entries(data.suggestions).map(([t, s]) => (
                  <div key={t} className="p-3 rounded-lg flex items-center gap-3 flex-wrap" style={{ background: "var(--wofy-surface)" }} data-testid={`threshold-${t}`}>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium">{t}</div>
                      <div className="text-xs opacity-70">
                        False rejects: {s.false_positive_rejections} · False approvals: {s.false_positive_approvals}
                      </div>
                    </div>
                    <div className="text-sm inline-flex items-center gap-1">
                      {s.net_override > 0 && <TrendingDown className="w-4 h-4 text-green-700" title="AI too strict — lowering threshold" />}
                      {s.net_override < 0 && <TrendingUp className="w-4 h-4 text-red-700" title="AI too lenient — raising threshold" />}
                      {s.net_override === 0 && <Minus className="w-4 h-4 opacity-50" />}
                      <span className="font-mono">0.90 → <b>{s.recommended_auto_approve}</b></span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>

          {/* Recent events */}
          {data.recent?.length > 0 && (
            <section className="wofy-card !p-5" data-testid="feedback-log">
              <h2 className="font-display text-xl mb-3">Recent overrides</h2>
              <ul className="text-sm space-y-2 max-h-96 overflow-y-auto">
                {data.recent.map((r, i) => (
                  <li key={i} className="p-2 rounded flex items-center gap-3" style={{ background: "var(--wofy-surface)" }}>
                    <span className={`text-[10px] font-mono px-2 py-0.5 rounded-full ${
                      r.override?.startsWith("approve") ? "bg-green-200 text-green-900" : "bg-red-200 text-red-900"
                    }`}>{r.override}</span>
                    <span className="text-xs opacity-70">confidence: <b>{(r.confidence || 0).toFixed(2)}</b></span>
                    <span className="text-xs opacity-70 truncate">by {r.moderator_name}</span>
                    <span className="text-xs opacity-50 ml-auto">{new Date(r.created_at).toLocaleString()}</span>
                  </li>
                ))}
              </ul>
            </section>
          )}
        </>
      )}
    </div>
  );
}
